/**
 * The chart itself: layout, scales, chrome, hover, table view.
 *
 * Rules this file enforces rather than documents:
 *  - no dual y-axis, ever (`assertNoDualAxis`)
 *  - a legend whenever there are >= 2 series, and it cannot be switched off
 *  - on light surfaces the palette carries a non-dismissable contrast warning,
 *    so legend + direct labels + the table affordance are forced
 *  - all text is drawn in theme text tokens; the coloured swatch carries identity
 *  - the hover layer is on by default; only a sparkline / bare stat tile opts out
 */

import {
  autoDomain,
  bandScale,
  formatTick,
  niceTicks,
  scaleLinear,
  unionExtent,
} from "./charts-scale.mjs";
import type { BandScale } from "./charts-scale.mjs";
import { stackExtent, stackSeries } from "./charts-stack.mjs";
import type { StackBand } from "./charts-stack.mjs";
import { drawReferenceLines, referenceExtent } from "./charts-reference.mjs";
import { resolveSeriesColors } from "./charts-palette.mjs";
import { AXIS_FONT_PX, drawAxisRule, drawPositionAxis, drawValueAxis, measureValueAxis } from "./axes";
import { ChartLayers } from "./layers";
import { getSeriesType } from "./series";
import { ensureChartCss } from "./styles";
import { resolveChartTheme } from "./theme";
import type {
  ChartHandle,
  ChartMode,
  ChartOptions,
  ChartPoint,
  ChartPointRef,
  ChartSeriesSpec,
  ChartTable,
  ChartTheme,
  ChartTooltipRow,
  Rect,
  ResolvedPoint,
  SeriesPlotContext,
  SeriesRenderer,
} from "./types";

interface LiveSeries {
  key: string;
  label: string;
  type: string;
  renderer: SeriesRenderer;
  points: ResolvedPoint[];
  hidden: boolean;
  color: string;
  stack: StackBand[] | null;
  groupIndex: number;
  groupCount: number;
  dash: boolean;
  fill: "none" | "gradient";
  endTag: boolean;
}

const PAD = { top: 8, right: 14, bottom: 8, left: 8 };

/**
 * Dual y-scales are not an option this library declines to default to — they
 * are an option it does not have. Two arbitrary ranges make the crossing point
 * an artefact of scaling, and readers reliably misread it as a real event.
 */
function assertNoDualAxis(opts: Record<string, unknown>) {
  for (const banned of ["y2", "yAxes", "secondaryAxis", "rightAxis", "yRight"]) {
    if (banned in opts) {
      throw new Error(
        `charts: "${banned}" is not supported — this library has no dual-axis mode. ` +
          `Two y-scales make the crossing point an artefact of scaling. Use two stacked ` +
          `charts sharing an x-axis, or index both series to a common baseline.`
      );
    }
  }
}

export class Chart implements ChartHandle {
  readonly el: HTMLElement;

  private head: HTMLElement;
  private titleEl: HTMLElement;
  private subtitleEl: HTMLElement;
  private legendEl: HTMLElement;
  private tableBtn: HTMLButtonElement;
  private bodyEl: HTMLElement;
  private tipEl: HTMLElement;
  private tableWrap: HTMLElement;

  private layers: ChartLayers;
  private opts: ChartOptions;
  private specs: ChartSeriesSpec[];
  private categories: string[] = [];
  private live: LiveSeries[] = [];
  /** Entity → palette slot. Survives setSeries so colour follows the entity. */
  private slots: Record<string, number> = {};
  private theme: ChartTheme;
  private plot: Rect = { x: 0, y: 0, width: 0, height: 0 };
  private valueAt: (v: number) => number = () => 0;
  private posAt: (v: number) => number = () => 0;
  private band: BandScale | null = null;
  private valueTicks: number[] = [];
  private valueStep = 1;
  private baseline = 0;
  private hoverIndex: number | null = null;
  private hoverSeries: string | null = null;
  private unsubscribeTheme: (() => void) | null = null;
  private destroyed = false;
  private tableVisible = false;
  private warnedInteractive = false;

  constructor(options: ChartOptions, subscribeTheme?: (fn: () => void) => () => void) {
    assertNoDualAxis(options as unknown as Record<string, unknown>);
    ensureChartCss();
    this.opts = { ...options };
    this.specs = (options.series || []).map((s) => ({ ...s }));

    this.el = document.createElement("div");
    this.el.className = "os-chart";
    if (options.kind === "sparkline") this.el.classList.add("os-chart--spark");

    this.head = document.createElement("div");
    this.head.className = "os-chart-head";
    const titles = document.createElement("div");
    titles.className = "os-chart-titles";
    this.titleEl = document.createElement("div");
    this.titleEl.className = "os-chart-title";
    this.subtitleEl = document.createElement("div");
    this.subtitleEl.className = "os-chart-sub";
    titles.append(this.titleEl, this.subtitleEl);
    this.legendEl = document.createElement("div");
    this.legendEl.className = "os-chart-legend";
    this.tableBtn = document.createElement("button");
    this.tableBtn.type = "button";
    this.tableBtn.className = "os-chart-tablebtn";
    this.tableBtn.textContent = "Table";
    this.tableBtn.title = "Show the same numbers as a table";
    this.tableBtn.addEventListener("click", () => this.setTableVisible(!this.tableVisible));
    this.head.append(titles, this.legendEl, this.tableBtn);

    this.bodyEl = document.createElement("div");
    this.bodyEl.className = "os-chart-body";
    // `height` sizes the PLOT, not the whole component. Putting it on the root
    // made the head and the table compete with the canvas inside a fixed box:
    // opening the table crushed the plot and clipped the table's own rows.
    if (options.height) {
      this.bodyEl.style.height = options.height + "px";
      this.bodyEl.style.flex = "0 0 auto";
      this.el.style.height = "auto";
    }
    this.tipEl = document.createElement("div");
    this.tipEl.className = "os-chart-tip";
    this.tipEl.hidden = true;

    this.tableWrap = document.createElement("div");
    this.tableWrap.className = "os-chart-tablewrap";
    this.tableWrap.hidden = true;

    this.layers = new ChartLayers(
      (dirty) => this.paint(dirty),
      () => this.layout()
    );
    this.bodyEl.append(this.layers.el, this.tipEl);
    this.el.append(this.head, this.bodyEl, this.tableWrap);

    this.theme = resolveChartTheme(this.el, this.opts.theme || "auto");

    if (options.host) options.host.appendChild(this.el);

    // Assigned directly, not via setCategories, so the first build happens once.
    this.categories = [...(options.categories || [])];
    this.rebuild();

    this.layers.observe(this.bodyEl);
    if (this.interactive()) this.bindPointer();

    // Follow the OS theme. Without this a chart keeps dark hues after a switch
    // to theme-windows-classic, which is the exact failure to avoid.
    if (subscribeTheme) this.unsubscribeTheme = subscribeTheme(() => this.refreshTheme());
    this.layers.markAll();
  }

  /* ── public handle ─────────────────────────────────────────────────────── */

  setSeries(series: ChartSeriesSpec[]) {
    this.specs = (series || []).map((s) => ({ ...s }));
    this.rebuild();
    this.layers.markAll();
  }

  setData(data: Record<string, ChartSeriesSpec["data"]> | ChartSeriesSpec["data"][]) {
    if (Array.isArray(data)) {
      this.specs = this.specs.map((s, i) => ({ ...s, data: data[i] || [] }));
    } else {
      this.specs = this.specs.map((s) => {
        const next = data[s.key || s.label];
        return next ? { ...s, data: next } : s;
      });
    }
    this.rebuild();
    this.layers.markAll();
  }

  setTitle(title: string) {
    this.opts.title = title;
    this.paintChrome();
  }

  setCategories(categories: ReadonlyArray<string>) {
    this.categories = [...(categories || [])];
    this.rebuild();
    this.layers.markAll();
  }

  resize() {
    if (this.layers.measure()) this.layout();
    this.layers.markAll();
  }

  getMode(): ChartMode {
    return this.theme.mode;
  }

  setTableVisible(visible: boolean) {
    this.tableVisible = visible;
    this.tableWrap.hidden = !visible;
    this.tableBtn.setAttribute("aria-expanded", String(visible));
    this.tableBtn.classList.toggle("is-on", visible);
    if (visible) {
      this.tableWrap.replaceChildren(this.toTable().el);
    } else {
      this.tableWrap.replaceChildren();
    }
    this.resize();
  }

  toTable(): ChartTable {
    const visible = this.live;
    const columns: string[] = [this.opts.x?.label || "Category"];
    for (const s of visible) {
      const extra = s.renderer.columns?.(s.label);
      if (extra && extra.length) columns.push(...extra);
      else columns.push(s.label);
    }

    const length = visible.reduce((m, s) => Math.max(m, s.points.length), 0);
    const rows: Array<Array<string | number | null>> = [];
    for (let i = 0; i < length; i++) {
      const first = visible.find((s) => s.points[i]);
      const row: Array<string | number | null> = [first ? first.points[i].label : String(i)];
      for (const s of visible) {
        const p = s.points[i];
        if (!p) {
          row.push(null);
          continue;
        }
        const cells = s.renderer.cells?.(p);
        if (cells && cells.length) row.push(...cells);
        else row.push(p.y);
      }
      rows.push(row);
    }

    const table = document.createElement("table");
    table.className = "os-chart-table";
    if (this.opts.title) {
      const cap = document.createElement("caption");
      cap.textContent = this.opts.title;
      table.appendChild(cap);
    }
    const thead = document.createElement("thead");
    const htr = document.createElement("tr");
    for (const c of columns) {
      const th = document.createElement("th");
      th.scope = "col";
      th.textContent = c;
      htr.appendChild(th);
    }
    thead.appendChild(htr);
    const tbody = document.createElement("tbody");
    for (const r of rows) {
      const tr = document.createElement("tr");
      r.forEach((cell, ci) => {
        const td = document.createElement(ci === 0 ? "th" : "td");
        if (ci === 0) (td as HTMLTableCellElement).scope = "row";
        td.textContent = cell == null ? "—" : typeof cell === "number" ? this.fmt(cell) : cell;
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    }
    table.append(thead, tbody);

    const wrap = document.createElement("div");
    wrap.className = "os-chart-tablescroll";
    wrap.appendChild(table);

    return {
      columns,
      rows,
      el: wrap,
      toCsv: () => {
        const esc = (v: string | number | null) => {
          const s = v == null ? "" : String(v);
          return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
        };
        return [columns.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\r\n");
      },
    };
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.unsubscribeTheme?.();
    this.unsubscribeTheme = null;
    this.unbindPointer();
    this.layers.destroy();
    this.el.remove();
  }

  /* ── theme ─────────────────────────────────────────────────────────────── */

  private refreshTheme() {
    if (this.destroyed) return;
    // Re-resolve from the LIVE token: a theme switch changes --os-window-body,
    // and the palette must follow the surface it is actually drawn on.
    this.theme = resolveChartTheme(this.el, this.opts.theme || "auto");
    this.rebuild();
    this.layers.markAll();
  }

  /* ── data ──────────────────────────────────────────────────────────────── */

  private rebuild() {
    const visibleSpecs = this.specs.filter((s) => !s.hidden);
    const keys = this.specs.map((s) => s.key || s.label);

    // Built from `this.specs`, same as `keys` — not `visibleSpecs` — so hiding
    // a series never frees its pinned slot for another series to claim.
    const pinned: Record<string, number> = {};
    for (const s of this.specs) {
      if (Number.isInteger(s.slot)) pinned[s.key || s.label] = s.slot as number;
    }

    // Colour follows the entity: `previous` carries the slots forward, so
    // hiding or re-sorting series never repaints the survivors. `pinned`
    // outranks `previous` — the caller has named the hue outright, while a
    // slot carried forward from a prior render is only a default. Range and
    // collision checks happen inside `assignSlots`; nothing is duplicated here.
    const { colors, slots } = resolveSeriesColors(keys, {
      previous: this.slots,
      pinned,
      overflow: this.opts.overflow === "other" ? "other" : "error",
      mode: this.theme.mode,
    });
    this.slots = slots;

    // Categories come from the option, else from the first string x values seen.
    if (!this.categories.length) {
      const derived: string[] = [];
      const seen = new Set<string>();
      for (const s of this.specs) {
        for (const d of s.data || []) {
          if (d && typeof d === "object" && "x" in d) {
            const x = (d as ChartPoint).x;
            if (typeof x === "string" && !seen.has(x)) {
              seen.add(x);
              derived.push(x);
            }
          }
        }
      }
      if (derived.length) this.categories = derived;
    }

    const defaultType = this.chartType();
    const built: LiveSeries[] = visibleSpecs.map((spec) => {
      const key = spec.key || spec.label;
      const type = spec.type || defaultType;
      const renderer = getSeriesType(type);
      return {
        key,
        label: spec.label,
        type,
        renderer,
        points: this.resolvePoints(spec.data),
        hidden: false,
        color: colors[key] || this.theme.other,
        stack: null,
        groupIndex: 0,
        groupCount: 1,
        dash: spec.dash === true,
        fill: spec.fill ?? "none",
        endTag: spec.endTag === true,
      };
    });

    // Group siblings of the same type so grouped bars know their slot.
    const byType = new Map<string, LiveSeries[]>();
    for (const s of built) {
      const list = byType.get(s.type) || [];
      list.push(s);
      byType.set(s.type, list);
    }
    for (const list of byType.values()) {
      list.forEach((s, i) => {
        s.groupIndex = i;
        s.groupCount = list.length;
      });
    }

    // Stacking, per type, over the stackable members only.
    if (this.opts.stacked) {
      for (const list of byType.values()) {
        const stackable = list.filter((s) => s.renderer.stackable);
        if (stackable.length < 1) continue;
        const bands = stackSeries(
          stackable.map((s) => s.points.map((p) => p.y)),
          { offset: this.opts.stacked === "expand" ? "expand" : "zero" }
        );
        stackable.forEach((s, i) => {
          s.stack = bands[i];
          s.groupIndex = i;
          s.groupCount = stackable.length;
        });
      }
    }

    this.live = built;
    this.layout();
    this.paintChrome();
  }

  private resolvePoints(data: ChartSeriesSpec["data"]): ResolvedPoint[] {
    const out: ResolvedPoint[] = [];
    const banded = this.isBanded();
    (data || []).forEach((d, i) => {
      if (d == null || typeof d === "number") {
        out.push({
          x: i,
          y: typeof d === "number" && Number.isFinite(d) ? d : null,
          label: this.categories[i] ?? String(i),
        });
        return;
      }
      const p = d as ChartPoint;
      const rawX = p.x instanceof Date ? p.x.getTime() : p.x;
      let x: number;
      let label: string;
      if (typeof rawX === "string") {
        const idx = this.categories.indexOf(rawX);
        x = idx >= 0 ? idx : i;
        label = rawX;
      } else if (typeof rawX === "number" && Number.isFinite(rawX)) {
        x = rawX;
        label = p.x instanceof Date ? p.x.toLocaleDateString() : formatTick(rawX, 1);
      } else {
        x = i;
        label = this.categories[i] ?? String(i);
      }
      if (banded) x = typeof rawX === "string" ? x : i;
      out.push({
        x,
        y: typeof p.y === "number" && Number.isFinite(p.y) ? p.y : null,
        label,
        meta: p.meta,
      });
    });
    return out;
  }

  private chartType(): string {
    const kind = this.opts.kind || "line";
    return kind === "statTile" ? "sparkline" : kind;
  }

  /** True when the position axis is categorical (any banded series, or categories given). */
  private isBanded(): boolean {
    if (this.categories.length) return true;
    const t = this.chartType();
    if (t === "bar") return true;
    return this.specs.some((s) => s.type === "bar");
  }

  /**
   * The hover layer is on by default and `interactive: false` is honoured only
   * for the two shapes the standard exempts — a sparkline has nothing to hover
   * and a bare stat tile is already the value. Anywhere else the flag is
   * ignored rather than obeyed: a bar chart you cannot interrogate is a picture
   * of data, not a chart. Warned once so it is not silent.
   */
  private interactive(): boolean {
    const kind = this.opts.kind || "line";
    const exempt = kind === "sparkline" || kind === "statTile";
    if (exempt) return false;
    if (this.opts.interactive === false && !this.warnedInteractive) {
      this.warnedInteractive = true;
      console.warn(
        `[charts] interactive:false is ignored for kind "${kind}" — only a sparkline or a ` +
          `bare stat tile may ship without a crosshair/tooltip layer.`
      );
    }
    return true;
  }

  private orientation(): "vertical" | "horizontal" {
    return this.opts.orientation === "horizontal" ? "horizontal" : "vertical";
  }

  private fmt(v: number): string {
    if (this.opts.valueFormat) return this.opts.valueFormat(v);
    return formatTick(v, this.valueStep || 1);
  }

  /* ── layout ────────────────────────────────────────────────────────────── */

  private layout() {
    const g = this.layers.context("plot");
    const w = this.layers.width;
    const h = this.layers.height;
    if (!g || w <= 0 || h <= 0) return;

    const spark = (this.opts.kind || "line") === "sparkline";
    const horizontal = this.orientation() === "horizontal";

    // Value domain — polymorphic: every series reports its own extent, so a
    // future candlestick contributes low..high without the chart knowing.
    const includeZero =
      this.opts.y?.includeZero ??
      this.live.some((s) => s.renderer.type === "bar" || s.renderer.type === "area");
    let domain: { min: number; max: number; step: number };
    if (this.opts.stacked && this.live.some((s) => s.stack)) {
      const e = unionExtent([
        stackExtent(this.live.filter((s) => s.stack).map((s) => s.stack as StackBand[])),
        referenceExtent(this.opts.reference),
      ]);
      domain = autoDomain(e ? [e.min, e.max] : [], {
        includeZero: true,
        maxTicks: 6,
        min: this.opts.y?.min,
        max: this.opts.y?.max,
      });
    } else {
      const e = unionExtent([
        ...this.live.map((s) => s.renderer.extent(s.points)),
        referenceExtent(this.opts.reference),
      ]);
      domain = autoDomain(e ? [e.min, e.max] : [], {
        includeZero,
        maxTicks: this.opts.y?.ticks || 6,
        min: this.opts.y?.min,
        max: this.opts.y?.max,
      });
    }
    this.valueStep = domain.step;
    this.valueTicks = spark ? [] : niceTicks(domain.min, domain.max, this.opts.y?.ticks || 6);

    // Reserve axis gutters from measured text, not from a guessed constant.
    let left = PAD.left;
    let bottom = PAD.bottom;
    if (!spark) {
      const axisW = horizontal
        ? this.longestCategoryWidth(g)
        : measureValueAxis(
            g,
            { ticks: this.valueTicks, step: domain.step, format: this.opts.y?.format },
            this.theme
          );
      left = PAD.left + axisW + 10;
      bottom = PAD.bottom + AXIS_FONT_PX + 10;
    }

    this.plot = {
      x: left,
      y: PAD.top,
      width: Math.max(0, w - left - PAD.right),
      height: Math.max(0, h - PAD.top - bottom),
    };

    const valueRange: [number, number] = horizontal
      ? [this.plot.x, this.plot.x + this.plot.width]
      : [this.plot.y + this.plot.height, this.plot.y];
    this.valueAt = scaleLinear([domain.min, domain.max], valueRange);
    this.baseline = this.valueAt(domain.min <= 0 && domain.max >= 0 ? 0 : domain.min);

    const posRange: [number, number] = horizontal
      ? [this.plot.y, this.plot.y + this.plot.height]
      : [this.plot.x, this.plot.x + this.plot.width];

    if (this.isBanded()) {
      const count = Math.max(
        this.categories.length,
        this.live.reduce((m, s) => Math.max(m, s.points.length), 0)
      );
      this.band = bandScale(count, posRange, { padInner: 0.24, padOuter: 0.12 });
      this.posAt = (x: number) => (this.band as BandScale).center(x);
    } else {
      this.band = null;
      const xs = this.live.flatMap((s) => s.points.map((p) => p.x));
      const xe = xs.length ? { min: Math.min(...xs), max: Math.max(...xs) } : { min: 0, max: 1 };
      this.posAt = scaleLinear([xe.min, xe.max], posRange);
    }
  }

  private longestCategoryWidth(g: CanvasRenderingContext2D): number {
    g.font = `${AXIS_FONT_PX}px ${this.theme.fontUi}`;
    const labels = this.categories.length
      ? this.categories
      : (this.live[0]?.points || []).map((p) => p.label);
    let w = 0;
    for (const l of labels) w = Math.max(w, g.measureText(l).width);
    return Math.min(Math.ceil(w), 160);
  }

  /* ── painting ──────────────────────────────────────────────────────────── */

  private paint(dirty: ReadonlySet<"plot" | "overlay">) {
    if (this.destroyed) return;
    if (dirty.has("plot")) this.paintPlot();
    if (dirty.has("overlay")) this.paintOverlay();
  }

  private paintPlot() {
    const g = this.layers.context("plot");
    if (!g) return;
    // Self-heal, generalised: RE-MEASURE before every plot paint, not just when
    // the plot rect came out empty.
    //
    // The original guard only caught the 0x0 case ("blank until you click
    // Table"). It cannot catch a **UI-scale change**, and neither can the
    // ResizeObserver: scale is `document.body.style.zoom`, which alters how the
    // box is PAINTED without altering the layout box anyone observes. So no
    // resize is ever delivered, and every chart on screen keeps drawing at the
    // old scale until something unrelated resizes it.
    //
    // `measure()` returns false when nothing changed, so the steady-state cost
    // is one getBoundingClientRect per dirty paint. `markAll` is needed
    // because resizing a canvas clears BOTH layers, and the overlay would
    // otherwise stay blank until the next hover.
    if (this.layers.measure()) {
      this.layout();
      this.layers.markAll();
    } else if ((this.plot.width <= 0 || this.plot.height <= 0) && this.layers.width > 0 && this.layers.height > 0) {
      this.layout();
    }
    if (this.plot.width <= 0 || this.plot.height <= 0) return;
    this.layers.clear("plot");
    const spark = (this.opts.kind || "line") === "sparkline";
    const horizontal = this.orientation() === "horizontal";

    if (!spark) {
      drawValueAxis(
        g,
        this.plot,
        {
          ticks: this.valueTicks,
          step: this.valueStep,
          valueAt: this.valueAt,
          format: this.opts.y?.format,
        },
        this.theme,
        horizontal ? "horizontal" : "vertical"
      );
      drawPositionAxis(
        g,
        this.plot,
        {
          labels: this.isBanded()
            ? this.categories.length
              ? this.categories
              : (this.live[0]?.points || []).map((p) => p.label)
            : undefined,
          band: this.band,
          ticks: this.isBanded() ? undefined : this.positionTicks(),
          step: 1,
          posAt: this.posAt,
          format: this.opts.x?.format,
        },
        this.theme,
        horizontal ? "horizontal" : "vertical"
      );
      drawAxisRule(g, this.plot, this.theme);
    }

    if (this.opts.reference?.length) {
      drawReferenceLines(g, this.opts.reference, {
        plot: this.plot,
        valueAt: this.valueAt,
        theme: this.theme,
      });
    }

    g.save();
    g.beginPath();
    g.rect(this.plot.x - 1, this.plot.y - 1, this.plot.width + 2, this.plot.height + 2);
    g.clip();
    for (const s of this.live) {
      s.renderer.draw(s.points, this.plotContext(s));
    }
    g.restore();

    if (!spark) this.paintDirectLabels(g);
  }

  private positionTicks(): number[] {
    const xs = this.live.flatMap((s) => s.points.map((p) => p.x));
    if (!xs.length) return [];
    return niceTicks(Math.min(...xs), Math.max(...xs), 6);
  }

  private plotContext(s: LiveSeries): SeriesPlotContext {
    return {
      ctx2d: this.layers.context("plot") as CanvasRenderingContext2D,
      plot: this.plot,
      theme: this.theme,
      valueAt: this.valueAt,
      posAt: this.posAt,
      band: this.band,
      baseline: this.baseline,
      color: s.color,
      groupIndex: s.groupIndex,
      groupCount: s.groupCount,
      stack: s.stack,
      orientation: this.orientation(),
      hoverIndex: this.hoverSeries === null || this.hoverSeries === s.key ? this.hoverIndex : null,
      dash: s.dash,
      fill: s.fill,
      endTag: s.endTag,
      // Bound: `fmt` is a real class method (reads `this.opts`), and a bare
      // `this.fmt` reference would lose that binding once detached onto pc.
      format: (v: number) => this.fmt(v),
    };
  }

  /**
   * Direct labels, and only on selected marks. There is no "label every point"
   * mode — a number on every point is noise, not annotation. Labels are drawn
   * in the text token, never in the series colour.
   */
  private paintDirectLabels(g: CanvasRenderingContext2D) {
    const mode =
      this.opts.directLabels ?? (this.theme.reliefRequired && this.live.length ? "last" : "none");
    if (mode === "none") return;
    g.font = `600 ${AXIS_FONT_PX}px ${this.theme.fontUi}`;
    g.textBaseline = "middle";
    for (const s of this.live) {
      const pts = s.points.filter((p) => p.y != null);
      if (!pts.length) continue;
      const picks: ResolvedPoint[] = [pts[pts.length - 1]];
      if (mode === "extremes") {
        const hi = pts.reduce((a, b) => ((b.y as number) > (a.y as number) ? b : a));
        const lo = pts.reduce((a, b) => ((b.y as number) < (a.y as number) ? b : a));
        if (!picks.includes(hi)) picks.push(hi);
        if (!picks.includes(lo)) picks.push(lo);
      }
      for (const p of picks) {
        const i = s.points.indexOf(p);
        const x = this.band ? this.band.center(i) : this.posAt(p.x);
        const y = this.valueAt(p.y as number);
        const text = this.fmt(p.y as number);
        const w = g.measureText(text).width;
        const right = x + 6 + w <= this.plot.x + this.plot.width;
        g.textAlign = right ? "left" : "right";
        // A halo of surface keeps the label legible over its own mark without
        // tinting the text with the series hue.
        g.lineWidth = 3;
        g.strokeStyle = this.theme.surface;
        g.strokeText(text, right ? x + 6 : x - 6, y);
        g.fillStyle = this.theme.text;
        g.fillText(text, right ? x + 6 : x - 6, y);
      }
    }
  }

  private paintOverlay() {
    const g = this.layers.context("overlay");
    if (!g) return;
    this.layers.clear("overlay");
    if (this.hoverIndex == null || !this.interactive()) return;
    const shared = this.live.some((s) => s.renderer.hover === "shared");
    if (!shared) return;

    const first = this.live[0];
    if (!first) return;
    const i = this.hoverIndex;
    const p = first.points[i];
    if (!p) return;
    const x = this.band ? this.band.center(i) : this.posAt(p.x);
    g.strokeStyle = this.theme.crosshair;
    g.lineWidth = 1;
    g.setLineDash([3, 3]);
    g.beginPath();
    g.moveTo(Math.round(x) + 0.5, this.plot.y);
    g.lineTo(Math.round(x) + 0.5, this.plot.y + this.plot.height);
    g.stroke();
    g.setLineDash([]);
  }

  /**
   * Chrome: title, legend and the table affordance.
   *
   * The legend is not optional above one series, and there is no API to turn it
   * off. With exactly one series the title names it, so a legend would just
   * repeat the heading — except on a light surface, where the contrast warning
   * obliges a visible label regardless.
   */
  private paintChrome() {
    this.titleEl.textContent = this.opts.title || "";
    this.titleEl.hidden = !this.opts.title;
    this.subtitleEl.textContent = this.opts.subtitle || "";
    this.subtitleEl.hidden = !this.opts.subtitle;

    const needLegend =
      this.specs.length >= 2 || (this.theme.reliefRequired && this.specs.length >= 1);
    this.legendEl.replaceChildren();
    this.legendEl.hidden = !needLegend;
    if (needLegend) {
      for (const s of this.specs) {
        const key = s.key || s.label;
        const item = document.createElement("span");
        item.className = "os-chart-legenditem";
        if (s.hidden) item.classList.add("is-off");
        const sw = document.createElement("i");
        sw.className = "os-chart-swatch";
        // The swatch carries identity. The label is a text token.
        sw.style.background = this.live.find((l) => l.key === key)?.color || this.theme.other;
        const txt = document.createElement("span");
        txt.textContent = s.label;
        item.append(sw, txt);
        this.legendEl.appendChild(item);
      }
    }

    const spark = (this.opts.kind || "line") === "sparkline";
    // A table view must be reachable for any chart. Sparklines have no chrome
    // of their own, so their table is reached through `handle.toTable()`.
    this.tableBtn.hidden = spark;
    this.head.hidden = spark && !this.opts.title;
  }

  /* ── hover ─────────────────────────────────────────────────────────────── */

  private onMove = (ev: PointerEvent) => {
    const rect = this.layers.el.getBoundingClientRect();
    // Both the rect and clientX/Y are ZOOMED pixels, but the plot rect is in
    // LAYOUT pixels (see ChartLayers.measure). Without dividing, hover is off
    // by the UI scale and the crosshair trails the cursor across the plot.
    const zoom = this.layers.zoom || 1;
    const px = (ev.clientX - rect.left) / zoom;
    const py = (ev.clientY - rect.top) / zoom;
    if (
      px < this.plot.x ||
      px > this.plot.x + this.plot.width ||
      py < this.plot.y ||
      py > this.plot.y + this.plot.height
    ) {
      this.clearHover();
      return;
    }
    let bestIndex: number | null = null;
    let bestKey: string | null = null;
    let bestD = Infinity;
    for (const s of this.live) {
      if (s.renderer.hover === "none") continue;
      const hit = s.renderer.hitTest(s.points, px, py, this.plotContext(s));
      if (!hit) continue;
      if (s.renderer.hover === "shared") {
        // Shared hover: one index for every series at this position.
        const d = Math.abs(hit.px - px);
        if (d < bestD) {
          bestD = d;
          bestIndex = hit.index;
          bestKey = null;
        }
      } else {
        const d = Math.hypot(hit.px - px, hit.py - py);
        if (d < bestD) {
          bestD = d;
          bestIndex = hit.index;
          bestKey = s.key;
        }
      }
    }
    if (bestIndex === null) {
      this.clearHover();
      return;
    }
    if (bestIndex !== this.hoverIndex || bestKey !== this.hoverSeries) {
      this.hoverIndex = bestIndex;
      this.hoverSeries = bestKey;
      this.layers.markAll();
    }
    this.showTip(px, py);
  };

  private onLeave = () => this.clearHover();

  private onClick = (ev: PointerEvent) => {
    if (!this.opts.onPointClick || this.hoverIndex == null) return;
    const s = this.hoverSeries
      ? this.live.find((l) => l.key === this.hoverSeries)
      : this.live[0];
    const p = s?.points[this.hoverIndex];
    if (!s || !p) return;
    ev.preventDefault();
    const ref: ChartPointRef = {
      seriesKey: s.key,
      seriesLabel: s.label,
      index: this.hoverIndex,
      x: p.label,
      y: p.y,
      meta: p.meta,
    };
    this.opts.onPointClick(ref);
  };

  private bindPointer() {
    this.bodyEl.addEventListener("pointermove", this.onMove);
    this.bodyEl.addEventListener("pointerleave", this.onLeave);
    this.bodyEl.addEventListener("pointerdown", this.onClick);
  }

  private unbindPointer() {
    this.bodyEl.removeEventListener("pointermove", this.onMove);
    this.bodyEl.removeEventListener("pointerleave", this.onLeave);
    this.bodyEl.removeEventListener("pointerdown", this.onClick);
  }

  private clearHover() {
    this.tipEl.hidden = true;
    if (this.hoverIndex === null) return;
    this.hoverIndex = null;
    this.hoverSeries = null;
    this.layers.markAll();
  }

  private showTip(px: number, py: number) {
    const i = this.hoverIndex;
    if (i == null) return;
    const rows = this.hoverSeries
      ? this.live.filter((s) => s.key === this.hoverSeries)
      : this.live;
    const head = rows[0]?.points[i]?.label ?? "";
    this.tipEl.replaceChildren();
    const h = document.createElement("div");
    h.className = "os-chart-tiphead";
    h.textContent = head;
    this.tipEl.appendChild(h);
    for (const s of rows) {
      const p = s.points[i];
      const row = document.createElement("div");
      row.className = "os-chart-tiprow";
      const sw = document.createElement("i");
      sw.className = "os-chart-swatch";
      sw.style.background = s.color;
      const name = document.createElement("span");
      name.className = "os-chart-tipname";
      name.textContent = s.label;
      const val = document.createElement("span");
      val.className = "os-chart-tipval";
      val.textContent = p && p.y != null ? this.fmt(p.y) : "—";
      row.append(sw, name, val);
      this.tipEl.appendChild(row);
    }
    // Extra rows for a value the chart does not plot (a net, a ratio, a count
    // in `meta`). Built from `rows[0]`, which is already the right series in
    // both hover modes above: filtered to the hovered key for a per-mark
    // hover, or `this.live[0]` for a shared crosshair — the same choice
    // `onClick` makes for `ChartPointRef`.
    const first = rows[0];
    const firstPoint = first?.points[i];
    const ref: ChartPointRef = {
      seriesKey: first?.key ?? "",
      seriesLabel: first?.label ?? "",
      index: i,
      x: firstPoint?.label ?? "",
      y: firstPoint?.y ?? null,
      meta: firstPoint?.meta,
    };
    // Contained, unlike the per-series rows above: those are built entirely
    // from data the chart itself resolved and can be trusted. `tooltip.rows`
    // is someone else's code running inside our render path, mid-way through
    // building the tooltip (tipEl is already cleared, the series rows are
    // already appended) — a throw or a non-array return must lose only the
    // extra rows, not leave the tooltip stale/empty because `hidden = false`
    // below never ran. `console.warn` matches this file's existing style for
    // a caller-facing problem that shouldn't be silent but also isn't fatal
    // (see `interactive()` above).
    let extraRows: ChartTooltipRow[] = [];
    try {
      const result = this.opts.tooltip?.rows?.(ref);
      if (Array.isArray(result)) extraRows = result;
      else if (result !== undefined) {
        console.warn("[charts] tooltip.rows() must return an array; ignoring its result.");
      }
    } catch (e) {
      console.warn("[charts] tooltip.rows() threw; showing the chart's own rows only.", e);
    }
    for (const r of extraRows) {
      const row = document.createElement("div");
      row.className = "os-chart-tiprow";
      const name = document.createElement("span");
      name.className = "os-chart-tipname";
      name.textContent = r.label;
      const val = document.createElement("span");
      val.className = "os-chart-tipval";
      // No swatch: an extra row is not a series and must not borrow a
      // series' identity mark. `r.label` above is always present, so a status
      // hue here is the redundant channel `statusColor` requires before it
      // will hand one out — not colour standing alone.
      val.style.color =
        r.tone === "success" ? this.theme.status.good
        : r.tone === "caution" ? this.theme.status.warning
        : r.tone === "danger" ? this.theme.status.critical
        : r.tone === "dim" ? this.theme.textDim
        : this.theme.text;
      val.textContent = r.value;
      row.append(name, val);
      this.tipEl.appendChild(row);
    }
    this.tipEl.hidden = false;
    // Flip before the tooltip runs off the plot; measured, not guessed.
    const w = this.tipEl.offsetWidth;
    const hgt = this.tipEl.offsetHeight;
    const left = px + 14 + w > this.layers.width ? px - 14 - w : px + 14;
    const top = Math.max(0, Math.min(py - hgt / 2, this.layers.height - hgt));
    this.tipEl.style.transform = `translate(${Math.max(0, left)}px, ${top}px)`;
  }
}
